/**
 * Fullscreen editor for dashboard text widgets.
 *
 * Opened from the expand icon on a text tile. Shows the formatted content at
 * full size with the rich-text toolbar, so long briefing text can be written
 * without squinting at a small tile or digging through widget settings.
 */
import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Dialog,
  TextField,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import { useFeatureFlagsStore } from '@signals/app-shell/stores/featureFlagsStore';
import RichTextEditor from './RichTextEditor';

const CUI_BANNER_HEIGHT = 28;

export interface TextWidgetFullscreenEditorProps {
  open: boolean;
  /** Current widget title. */
  title: string;
  /** Current widget body (HTML or plain text). */
  body: string;
  onClose: () => void;
  onSave: (title: string, body: string) => void;
}

const TextWidgetFullscreenEditor: React.FC<TextWidgetFullscreenEditorProps> = ({
  open,
  title,
  body,
  onClose,
  onSave,
}) => {
  const theme = useTheme();
  const classificationBannerMode = useFeatureFlagsStore((s) => s.classificationBannerMode);
  const bannerH = classificationBannerMode === 'commercial' ? 0 : CUI_BANNER_HEIGHT;

  const [draftTitle, setDraftTitle] = useState(title);
  const [draftBody, setDraftBody] = useState(body);

  // Re-seed drafts each time the dialog opens for a widget.
  useEffect(() => {
    if (open) {
      setDraftTitle(title);
      setDraftBody(body);
    }
  }, [open, title, body]);

  const handleSave = () => {
    onSave(draftTitle, draftBody);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'background.default',
          pt: `${bannerH}px`,
          pb: `${bannerH}px`,
        },
      }}
      sx={{ zIndex: theme.zIndex.modal + 1 }}
    >
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar variant="dense" sx={{ gap: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flexShrink: 0 }}>
            Text Editor
          </Typography>
          <TextField
            size="small"
            label="Title"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            sx={{ flex: 1, maxWidth: 480 }}
          />
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          p: { xs: 1.5, md: 3 },
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 980,
            // Let the editor fill the page height; Quill scrolls internally.
            '& .ql-container': { fontSize: 15 },
            '& .ql-editor': { minHeight: '60vh' },
          }}
        >
          <RichTextEditor
            value={draftBody}
            onChange={setDraftBody}
            placeholder="Enter formatted content…"
            minHeight={Math.max(360, typeof window !== 'undefined' ? window.innerHeight - 260 : 480)}
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default TextWidgetFullscreenEditor;
